<template>
  <UsersTable
    :rows="users"
    :loading="isLoading || isDeletingUser"
    :page="currentPage"
    :searchEnabled="true"
    :searchQuery="searchQuery"
    :key="actionsCompleted"
    :has-more-items="hasNextPage"
    :removable="true"
    :editable="true"
    @click-row="redirectToUser"
    @load="fetchNextPage"
    @search="search($event)"
    @create="showCreateUserModal = true"
    @create-machine="showCreateMachineUserModal = true"
    @move="showMoveUserModal = true"
    @remove="
      (row: User) => {
        activeUser = row;
        showDeleteUserModal = true;
      }
    "
    @edit="
      (row: User) => {
        activeUser = row;
        showUpdateUserModal = true;
      }
    "
  />

  <!-- CREATE -->
  <MutationUserModal
    v-if="showCreateUserModal"
    @close="showCreateUserModal = false"
    @create="onCreateUser"
    :loading="isCreatingUser"
    :navigation-level="UserLevel.ORGANISATION"
    :navigation-parent-id="model.id"
  />

  <!-- UPDATE -->
  <MutationUserModal
    v-if="showUpdateUserModal && activeUser"
    :user="activeUser"
    :loading="isUpdatingUser"
    @close="closeUpdateModal"
    @update="onUpdateUser"
    :navigation-level="UserLevel.ORGANISATION"
    :navigation-parent-id="model.id"
  />

  <InfuseConfirmModal
    v-if="showDeleteUserModal"
    :action-title="t('iam.users.header.delete_user')"
    :action-description="t('iam.users.info.delete_user', { user: activeUser?.email || '' })"
    verification-text="delete"
    @confirm="onDeleteUser"
    @close="closeDeleteModal"
  />

  <MoveUserModal
    v-if="showMoveUserModal"
    :current-level="UserLevel.ORGANISATION"
    :current-organisation-id="model.id"
    :current-tenant-id="undefined"
    :loading="isMovingUsers"
    size="large"
    @close="showMoveUserModal = false"
    @move="onMoveUsers"
  />

  <MachineUserCreateFlow
    v-if="model"
    :show="showCreateMachineUserModal"
    :create="createMachineUser"
    :parent-id="model.id"
    :navigation-level="UserLevel.ORGANISATION"
    :loading="isCreatingMachineUser"
    @close="showCreateMachineUserModal = false"
    @created="refetch"
  />
</template>

<script setup lang="ts">
import { useOrganisationUsers } from '@infuse/iam/organisation/queries';
import { ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { MachineUserCreateFlow, MutationUserModal, MoveUserModal, UsersTable } from '@infuse/iam/user/ui';
import { CreateUser, UpdateUser, User } from '@infuse/iam/user/type';
import {
  useCreateOrganisationUser,
  useCreateOrganisationMachineUser,
  useDeleteOrganisationUser,
  useUpdateOrganisationUser
} from '@infuse/iam/organisation/mutations';
import { useMoveUsers } from '@infuse/iam/user/mutations';
import { Organisation } from '@infuse/api/iam/organisation';
import { InfuseConfirmModal } from '@infuse/ui/components';
import { useI18n } from 'vue-i18n';
import { UserLevel, UserMoveDestinationType } from '@infuse/iam/user/type';

const props = defineProps<{
  model: Organisation;
}>();

defineEmits(['reloadModel']);

const { t } = useI18n();

const {
  fetchParentId: fetchOrganisationId,
  users,
  currentPage,
  isLoading,
  hasNextPage,
  fetchNextPage,
  searchQuery,
  refetch
} = useOrganisationUsers();

const activeUser = ref<User | undefined>(undefined);

const { mutateAsync: createUser, isPending: isCreatingUser } = useCreateOrganisationUser();
const { mutateAsync: createMachineUser, isPending: isCreatingMachineUser } = useCreateOrganisationMachineUser();
const { mutateAsync: updateUser, isPending: isUpdatingUser } = useUpdateOrganisationUser();
const { mutateAsync: deleteUser, isPending: isDeletingUser } = useDeleteOrganisationUser();
const { mutateAsync: moveUsers, isPending: isMovingUsers } = useMoveUsers();

const router = useRouter();
const route = useRoute();

const actionsCompleted = ref(0);
const showCreateUserModal = ref(false);
const showCreateMachineUserModal = ref(false);
const showUpdateUserModal = ref(false);
const showDeleteUserModal = ref(false);
const showMoveUserModal = ref(false);
watch(
  route,
  () => {
    fetchOrganisationId.value = route.params.id as string;
  },
  { immediate: true }
);

watch(
  () => route.query.search,
  (search) => {
    searchQuery.value = search?.toString();
  },
  { immediate: true }
);

async function search(search: string): Promise<void> {
  searchQuery.value = search;
  replaceQueryParams(searchQuery.value);
}

function replaceQueryParams(searchQuery: string | undefined) {
  if (router.currentRoute.value.name !== 'user') {
    return;
  }
  router.replace({
    name: 'user',
    query: {
      search: searchQuery || undefined
    }
  });
}

function redirectToUser(row: User): void {
  let routeName = 'user.details';

  router.push({
    name: routeName,
    params: { id: row.id }
  });
}

async function onCreateUser(vars: { user: CreateUser }): Promise<void> {
  await createUser({ parentId: props.model.id, user: vars.user });
  showCreateUserModal.value = false;
  refetch();
}

async function onDeleteUser(): Promise<void> {
  if (!activeUser.value) {
    return;
  }
  await deleteUser({ parentId: props.model.id, id: activeUser.value.id });
  closeDeleteModal();
  refetch();
}

function closeDeleteModal() {
  showDeleteUserModal.value = false;
  activeUser.value = undefined;
}

async function onUpdateUser(vars: { id: string | undefined; user: CreateUser | UpdateUser }): Promise<void> {
  if (!vars.id || !('version' in vars.user)) {
    return;
  }
  await updateUser({ parentId: props.model.id, id: vars.id, user: vars.user });
  closeUpdateModal();
  refetch();
}

function closeUpdateModal() {
  showUpdateUserModal.value = false;
  activeUser.value = undefined;
}

async function onMoveUsers(data: {
  users: User[];
  destinationType: UserMoveDestinationType;
  destinationId?: string;
  targetTenantId?: string;
}) {
  try {
    await moveUsers({
      users: data.users,
      destinationType: data.destinationType,
      destinationId: data.destinationId,
      targetTenantId: data.targetTenantId
    });

    showMoveUserModal.value = false;
  } catch (error) {
    console.error('Error moving users:', error);
  }
}
</script>
